import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '../../lib/api'
import { 
  Bell, 
  Clock, 
  AlertTriangle, 
  CheckCircle,
  Trash2,
  Settings
} from 'lucide-react'
import { Link } from 'react-router-dom'

interface Notification {
  id: string
  type: 'expiring' | 'expired' | 'reminder' | 'info'
  title: string
  message: string
  isRead: boolean
  createdAt: string
  certificationId?: string
}

export default function NotificationsPage() {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['/api/notifications'],
    queryFn: () => apiGet<{ notifications: Notification[] }>('/notifications'),
  })

  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => 
      apiPost(`/notifications/${notificationId}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] })
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => apiPost('/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] })
    },
  })

  const notifications = data?.notifications || []
  const unreadCount = notifications.filter(n => !n.isRead).length

  const getIcon = (type: string) => {
    switch (type) {
      case 'expiring':
        return <Clock className="w-5 h-5 text-yellow-500" />
      case 'expired':
        return <AlertTriangle className="w-5 h-5 text-red-500" />
      case 'reminder':
        return <Bell className="w-5 h-5 text-blue-500" />
      default:
        return <CheckCircle className="w-5 h-5 text-green-500" />
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <button
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              Mark all as read
            </button>
          )}
          <Link
            to="/profile/notifications"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Settings className="w-4 h-4" />
            Settings
          </Link>
        </div>
      </div>

      {/* Notifications List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Notifications</h3>
          <p className="text-gray-500">
            You're all caught up! We'll notify you about important updates.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-200">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => !notification.isRead && markAsReadMutation.mutate(notification.id)}
              className={`flex items-start gap-4 p-4 cursor-pointer transition-colors ${
                notification.isRead ? 'bg-white' : 'bg-blue-50 hover:bg-blue-100'
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">
                {getIcon(notification.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className={`text-sm ${notification.isRead ? 'text-gray-900' : 'font-medium text-gray-900'}`}>
                    {notification.title}
                  </h3>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {formatDate(notification.createdAt)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {notification.message}
                </p>
                {notification.certificationId && (
                  <Link
                    to={`/certifications/${notification.certificationId}`}
                    className="inline-block text-sm text-blue-600 hover:text-blue-700 mt-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View certification →
                  </Link>
                )}
              </div>
              {!notification.isRead && (
                <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-2" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
